import { observable, action } from 'mobx';
import gql from 'graphql-tag';
import { create, persist } from 'mobx-persist/lib/index';
import localForage from 'localforage';
import jsonLogic from 'json-logic-js';

const ReadNatLang = gql`
    query ReadNatLang(
        $Link: String
    ) {
        readNatLang(Link: $Link) {
            ID
            Title
            Questions {
                id
                prefixText
                suffixText
                jsonLogic
                type
                newLine
                label
                placeholder
                Options {
                    nodes {
                        id
                        title
                    }
                }
            },
            Answers {
                id
                jsonLogic
                type
                title
                content
                queryLink
            }
        }
    }
`;

export class NaturalLanguageStore {
    @observable loading = true;
    @observable errorMessage = false;
    @observable hydrated = false;
    @observable hasLogic = false;

    @persist @observable pageTitle = false;
    @observable questions = [];
    @observable answers = [];
    @observable queryValues = [];

    constructor() {
        const hydrate = create({
            jsonify: true,
            storage: localForage
        });

        hydrate('natlang', this).then(() => {
            this.hydrated = true;
        });
    }

    async initializeNatLang(httpStore, link) {
        this.loading = true;

        const result = await httpStore.apolloClient
            .query({
                query: ReadNatLang,
                variables: {
                    Link: link
                },
                fetchPolicy: 'no-cache'
                // fetchPolicy: 'cache-first'
            })
            .catch(result => {
                const errors = result.graphQLErrors.map(error => error.message);
                this.errorMessage = errors.join(', ');
                this.loading = false;
            });

        if (typeof result !== 'undefined') {
            if (result.data.readNatLang) {
                const data = result.data.readNatLang,
                    search = window.location.search,
                    params = new URLSearchParams(search),
                    questionID = parseInt(params.get('Question'), 10),
                    value = parseInt(params.get('Answer'), 10);

                this.pageTitle = data.Title;

                this.questions = data.Questions;
                this.answers = data.Answers;
                this.loading = false;
                this.addValue(questionID, false, value);
                this.updateItems(this.questions);
                this.updateItems(this.answers);
            } else {
                if (typeof result.data !== 'undefined' && !result.loading) {
                    this.loading = false;
                }
            }
        } else {
            this.page = false;
            this.loading = false;
        }
    }

    @action updateItems(items) {
        for (const item of items) {
            if (items.includes(item)) {
                this.updateItemLogic(item, items);
            }
        }
        this.hasLogic = true;
    }

    @action addValue(questionID, isMultiple, value) {
        const index = this.queryValues.length > 0 ? this.queryValues.findIndex(obj => obj.question === questionID) : -1;

        if (index < 0) {
            this.queryValues.push({ 'question': questionID, 'option': parseInt(value, 10) });
            return;
        }

        if (isMultiple) {
            this.queryValues[index].option.push(parseInt(value, 10));
            return;
        }

        this.queryValues[index].option = parseInt(value, 10);
    }

    @action updateItemLogic(item, items) {
        const showIf = item.jsonLogic ? JSON.parse(item.jsonLogic) : true,
            data = this.queryValues.slice(),
            itemIndex = items.findIndex(obj => obj.id === item.id),
            answerIndex = this.queryValues.length > 0 ? this.queryValues.findIndex(obj => obj.question === item.id) : -1;

        items[itemIndex].Show = this.applyLogic(showIf, data);

        // if hidden, set value to false /remove value
        if (answerIndex > -1 && !items[itemIndex].Show) {
            this.queryValues[answerIndex] = {};
        }
    }

    @action applyLogic(rules, data) {
        if (!rules) return true;
        if (!data) return false;

        if (data.length === 0) {
            return Boolean(jsonLogic.apply(rules, data));
        }

        const result = Object.values(data);
        let logic;

        for (let i = 0; i < result.length; i++) {
            logic = Boolean(jsonLogic.apply(rules, result[i]));
            if (logic) {
                return true;
            }
        }

        return false;
    }
}